// Thin wrapper around the shared `apiFetch` helper, hard-coding the
// `app: 'video'` flag so each call lands on the video sub-app
// (`VITE_VIDEO_API_BASE`, which CI sets to `${LIGHTNING_API_BASE}/api/video`).
//
// All media URLs (rendered MP4, all-keyframes scrub MP4s, thumbnail) are
// served as `Blob` URLs after a fetch through `apiFetch` rather than being
// handed straight to `<video src=…>` / `<img src=…>`. Why:
//
// * The SPA is served from the static-bundle CDN host; the API is the API
//   Gateway host. They are different origins.
// * `apiFetch` attaches `Authorization: Bearer <id_token>` from
//   localStorage. `<video>` / `<img>` tags can't carry that header on a
//   cross-origin request, so the gateway returns 401 and the players
//   silently render nothing.
// * Going through `apiFetch` also avoids the gateway's CORS preflight
//   issue with the `HEAD` method (the previous low-res scrub probe used
//   HEAD, which the gateway's OpenAPI spec doesn't allow).
//
// Trade-off: blob fetching downloads the entire file before playback,
// whereas a raw `<video src=URL>` streams via HTTP Range. Acceptable
// today since the scrub MP4 needs the full file in cache anyway for
// instant seeks. If the regular playback video starts feeling sluggish
// on large files we should add a backend "signed URL" endpoint instead
// of bringing back the unauthenticated path.

import { apiFetch, VIDEO_API_BASE } from '../utils/api';
import type { DateRange } from '../types';
import type {
  BatchCreatedResponse,
  BatchHistoryResponse,
  BatchSummary,
  CreateScanResponse,
  GenerationLogResponse,
  HistoryResponse,
  HotspotsDocument,
  Job,
  PresetList,
  VideoDebugResponse,
  VideoGenConfig,
  VideoGenPreset,
  VideoGenSettings,
  VideoHealth,
} from './types';

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch {
      // body wasn't JSON; keep statusText
    }
    throw new Error(detail);
  }
  return (await res.json()) as T;
}

async function emptyOrThrow(res: Response): Promise<void> {
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch {
      // ignore
    }
    throw new Error(detail);
  }
}

// ----- mutations / fetches -----------------------------------------------

export type ImageSource = 'uvpaint' | 'lightning';

/**
 * The knobs a run is submitted with. Shared verbatim by single scans and CSV
 * batches, so a batch is "these settings, applied to every row".
 */
export interface ScanOptions {
  /** Defaults to `'uvpaint'`, matching the endpoint's own default. */
  imageSource?: ImageSource;
  /** A preset id, or `'default'` for whichever preset is the system default. */
  presetId?: string;
  /** Persist the annotated damage-localization images. `undefined` inherits
   *  the server's `VIDEO_DAMAGE_DEBUG` default. */
  damageDebug?: boolean;
  /** Compute hotspots (features + damages) as part of generation.
   *  `undefined` inherits the server default. */
  createHotspots?: boolean;
}

/** Body fields common to `POST /api/scans` and `POST /api/batches`. */
function scanOptionsBody(opts?: ScanOptions): Record<string, unknown> {
  // Both spelled out rather than left off, so the request says what it ran
  // with instead of relying on the server filling in a blank. These are the
  // endpoint's documented defaults.
  const body: Record<string, unknown> = {
    image_source: opts?.imageSource ?? 'uvpaint',
    preset_id: opts?.presetId || 'default',
  };
  // Omitted rather than sent as false, so an unknown value inherits the
  // server's default instead of forcing the feature off.
  if (opts?.damageDebug !== undefined) body.damage_debug = opts.damageDebug;
  if (opts?.createHotspots !== undefined) body.create_hotspots = opts.createHotspots;
  return body;
}

/** Submit a single manual scan. */
export async function createScan(
  scanId: string,
  opts?: ScanOptions,
): Promise<CreateScanResponse> {
  const res = await apiFetch('/api/scans', {
    app: 'video',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      scan_id: scanId,
      source: 'manual',
      ...scanOptionsBody(opts),
    }),
  });
  return jsonOrThrow<CreateScanResponse>(res);
}

// ----- batches ------------------------------------------------------------

/**
 * Submit a CSV batch. The caller extracts the scan IDs from the file (the
 * server takes a JSON list, matching lightning + interior), and `opts` is
 * applied identically to every scan in the batch.
 */
export async function submitBatch(
  filename: string,
  scanIds: string[],
  opts?: ScanOptions,
): Promise<BatchCreatedResponse> {
  const res = await apiFetch('/api/batches', {
    app: 'video',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      filename,
      scan_ids: scanIds,
      ...scanOptionsBody(opts),
    }),
  });
  return jsonOrThrow<BatchCreatedResponse>(res);
}

/** Batches with at least one job still pending or processing. */
export async function listActiveBatches(): Promise<BatchSummary[]> {
  const res = await apiFetch('/api/batches', { app: 'video' });
  return jsonOrThrow<BatchSummary[]>(res);
}

export async function listBatchHistory(opts?: {
  fromDate?: Date | null;
  toDate?: Date | null;
  page?: number;
  pageSize?: number;
}): Promise<BatchHistoryResponse> {
  const params = new URLSearchParams();
  if (opts?.fromDate) params.set('from_date', opts.fromDate.toISOString());
  if (opts?.toDate) params.set('to_date', opts.toDate.toISOString());
  params.set('page', String(opts?.page ?? 1));
  params.set('page_size', String(opts?.pageSize ?? 50));
  const res = await apiFetch(`/api/batches/history?${params.toString()}`, { app: 'video' });
  return jsonOrThrow<BatchHistoryResponse>(res);
}

/** Remove the batch row. Its jobs survive, with `batch_id` cleared. */
export async function deleteBatch(batchId: string): Promise<void> {
  const res = await apiFetch(`/api/batches/${encodeURIComponent(batchId)}`, {
    app: 'video',
    method: 'DELETE',
  });
  if (!res.ok && res.status !== 404) {
    throw new Error(`Failed to delete batch: ${res.status} ${res.statusText}`);
  }
}

// ----- jobs ---------------------------------------------------------------

export async function listLiveJobs(): Promise<Job[]> {
  const res = await apiFetch('/api/jobs/live', { app: 'video' });
  return jsonOrThrow<Job[]>(res);
}

/**
 * One page of terminal jobs. Signature matches interior's and ads' so the
 * shared history-table shape (date picker, status dropdown, scan-id/org
 * search, pagination) drives every app the same way.
 */
export async function listHistoryJobs(
  dateRange: DateRange,
  status: string | null,
  page: number,
  pageSize: number,
  scanId?: string | null,
  organizationId?: string | null,
): Promise<HistoryResponse> {
  const params = new URLSearchParams();
  if (dateRange.from) params.set('from_date', dateRange.from.toISOString());
  if (dateRange.to) params.set('to_date', dateRange.to.toISOString());
  if (status) params.set('status', status);
  if (scanId) params.set('scan_id', scanId);
  if (organizationId) params.set('organization_id', organizationId);
  params.set('page', String(page));
  params.set('page_size', String(pageSize));
  const res = await apiFetch(`/api/jobs/history?${params.toString()}`, { app: 'video' });
  return jsonOrThrow<HistoryResponse>(res);
}

/**
 * Every terminal job in the window, by walking the pages. Callers that
 * aggregate (statistics, cost, overview) need the whole set, not one page —
 * the same loop interior's and ads' hooks run.
 */
export async function listAllHistoryJobs(dateRange: DateRange): Promise<Job[]> {
  const all: Job[] = [];
  const pageSize = 500;
  let page = 1;
  let totalPages = 1;
  do {
    const resp = await listHistoryJobs(dateRange, null, page, pageSize);
    all.push(...resp.jobs);
    totalPages = resp.total_pages;
    if (resp.jobs.length === 0) break;
    page += 1;
  } while (page <= totalPages);
  return all;
}

export async function fetchVideoHealth(): Promise<VideoHealth> {
  const res = await apiFetch('/health', { app: 'video' });
  return jsonOrThrow<VideoHealth>(res);
}

export async function rerunJob(jobId: string): Promise<CreateScanResponse> {
  const res = await apiFetch(`/api/jobs/${encodeURIComponent(jobId)}/rerun`, {
    app: 'video',
    method: 'POST',
  });
  return jsonOrThrow<CreateScanResponse>(res);
}

export async function abortJob(jobId: string): Promise<void> {
  const res = await apiFetch(`/api/jobs/${encodeURIComponent(jobId)}/abort`, {
    app: 'video',
    method: 'POST',
  });
  return emptyOrThrow(res);
}

/** Abort every pending/processing job. Resolves with how many were hit. */
export async function abortAllLive(): Promise<{ aborted: number }> {
  const res = await apiFetch('/api/jobs/live', {
    app: 'video',
    method: 'DELETE',
  });
  return jsonOrThrow<{ aborted: number }>(res);
}

export async function deleteJob(jobId: string): Promise<void> {
  const res = await apiFetch(`/api/jobs/${encodeURIComponent(jobId)}`, {
    app: 'video',
    method: 'DELETE',
  });
  return emptyOrThrow(res);
}

export async function getVideoGenSettings(): Promise<VideoGenSettings> {
  const res = await apiFetch('/api/settings/video-gen', { app: 'video' });
  return jsonOrThrow<VideoGenSettings>(res);
}

export async function updateVideoGenSettings(config: VideoGenConfig): Promise<VideoGenConfig> {
  const res = await apiFetch('/api/settings/video-gen', {
    app: 'video',
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config),
  });
  return jsonOrThrow<VideoGenConfig>(res);
}

export async function resetVideoGenSettings(): Promise<VideoGenConfig> {
  const res = await apiFetch('/api/settings/video-gen/reset', {
    app: 'video',
    method: 'POST',
  });
  return jsonOrThrow<VideoGenConfig>(res);
}

// ----- Presets -----------------------------------------------------------

export async function listPresets(): Promise<PresetList> {
  const res = await apiFetch('/api/settings/video-gen/presets', { app: 'video' });
  return jsonOrThrow<PresetList>(res);
}

export async function createPreset(
  name: string,
  config?: VideoGenConfig,
): Promise<VideoGenPreset> {
  const res = await apiFetch('/api/settings/video-gen/presets', {
    app: 'video',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, ...(config ? { config } : {}) }),
  });
  return jsonOrThrow<VideoGenPreset>(res);
}

export async function updatePreset(
  presetId: string,
  patch: { name?: string; config?: VideoGenConfig },
): Promise<VideoGenPreset> {
  const res = await apiFetch(`/api/settings/video-gen/presets/${encodeURIComponent(presetId)}`, {
    app: 'video',
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  return jsonOrThrow<VideoGenPreset>(res);
}

export async function deletePreset(presetId: string): Promise<PresetList> {
  const res = await apiFetch(`/api/settings/video-gen/presets/${encodeURIComponent(presetId)}`, {
    app: 'video',
    method: 'DELETE',
  });
  return jsonOrThrow<PresetList>(res);
}

export async function setDefaultPreset(presetId: string): Promise<PresetList> {
  const res = await apiFetch(
    `/api/settings/video-gen/presets/${encodeURIComponent(presetId)}/default`,
    { app: 'video', method: 'POST' },
  );
  return jsonOrThrow<PresetList>(res);
}

// ----- Authenticated media fetchers --------------------------------------

/**
 * Fetch a media response and turn it into an object URL. Caller owns the
 * returned URL and is responsible for `URL.revokeObjectURL` once the
 * `<video>` / `<img>` element no longer needs it. Pass an `AbortSignal`
 * to cancel the in-flight download (e.g. when the user closes the modal
 * or scrolls a row out of view).
 */
async function fetchBlobUrl(path: string, signal?: AbortSignal): Promise<string> {
  const res = await apiFetch(path, { app: 'video', signal });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch {
      // ignore
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/**
 * Same shape as `fetchBlobUrl` but returns `null` on 404 / 410 instead
 * of throwing. Used by callers that have a meaningful fallback (the
 * 360 viewer treats "no low-res variant" as "drive the high-res
 * element directly").
 */
async function fetchBlobUrlOrNull(
  path: string,
  signal?: AbortSignal,
): Promise<string | null> {
  const res = await apiFetch(path, { app: 'video', signal });
  if (res.status === 404 || res.status === 410) return null;
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch {
      // ignore
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/** Inline-playback MP4. */
export function fetchVideoBlobUrl(jobId: string, signal?: AbortSignal): Promise<string> {
  return fetchBlobUrl(`/api/jobs/${encodeURIComponent(jobId)}/video`, signal);
}

/** All-keyframes companion MP4 used by the 360 viewer's drag scrub. */
export function fetchScrubVideoBlobUrl(jobId: string, signal?: AbortSignal): Promise<string> {
  return fetchBlobUrl(`/api/jobs/${encodeURIComponent(jobId)}/scrub-video`, signal);
}

/**
 * Low-resolution all-keyframes companion MP4. Cheaper to decode + seek
 * during a drag preview. Resolves to `null` for jobs from before the
 * variant existed; the viewer falls back to the full-res scrub MP4 in
 * that case (and the absence of a HEAD probe means no CORS preflight
 * grief at the gateway).
 */
export function fetchScrubVideoLowBlobUrl(
  jobId: string,
  signal?: AbortSignal,
): Promise<string | null> {
  return fetchBlobUrlOrNull(
    `/api/jobs/${encodeURIComponent(jobId)}/scrub-video-low`,
    signal,
  );
}

/** First-frame thumbnail JPEG used by the History table. */
export function fetchThumbnailBlobUrl(jobId: string, signal?: AbortSignal): Promise<string> {
  return fetchBlobUrl(`/api/jobs/${encodeURIComponent(jobId)}/thumbnail`, signal);
}

/**
 * Per-hotspot tracks for the 360 viewer's overlay. Resolves to `null`
 * when hotspots have not been computed for the job (404), so the viewer
 * degrades gracefully to a plain video with no overlay.
 */
export async function fetchHotspots(
  jobId: string,
  signal?: AbortSignal,
): Promise<HotspotsDocument | null> {
  const res = await apiFetch(`/api/jobs/${encodeURIComponent(jobId)}/hotspots`, {
    app: 'video',
    signal,
  });
  if (res.status === 404 || res.status === 410) return null;
  return jsonOrThrow<HotspotsDocument>(res);
}

/**
 * Trigger the (decoupled) hotspot calculation for a finished job.
 *
 * `features` (default true) re-detects the blue feature hotspots via
 * Moondream; pass `false` to reuse the existing ones untouched (cheap).
 * `damages` runs the heavier red-damage path. Whichever kind isn't
 * recomputed is carried over, so "+ Damages" (features:false, damages:true)
 * does not trigger a full feature re-detect.
 */
export async function computeHotspots(
  jobId: string,
  opts?: { features?: boolean; damages?: boolean },
): Promise<HotspotsDocument> {
  const params = new URLSearchParams();
  if (opts?.features === false) params.set('features', 'false');
  if (opts?.damages) params.set('damages', 'true');
  const qs = params.toString();
  const res = await apiFetch(
    `/api/jobs/${encodeURIComponent(jobId)}/hotspots/compute${qs ? `?${qs}` : ''}`,
    { app: 'video', method: 'POST' },
  );
  const body = await jsonOrThrow<{ document: HotspotsDocument }>(res);
  return body.document;
}

/**
 * Per-damage modal image (cropped original arch frame with the box drawn).
 * Fetched through `apiFetch` so the Bearer token is attached, then handed
 * to the modal as an object URL the caller must revoke.
 */
export function fetchHotspotImageBlobUrl(
  jobId: string,
  name: string,
  signal?: AbortSignal,
): Promise<string> {
  // `?v=` busts any entry poisoned by the old `max-age=86400` header; the
  // backend now sends `no-cache` so revalidation keeps the image fresh.
  return fetchBlobUrl(
    `/api/jobs/${encodeURIComponent(jobId)}/hotspots/images/${encodeURIComponent(name)}?v=2`,
    signal,
  );
}

/**
 * Manifest of a finished job's debug artifacts: the keyframe preprocessing
 * stages, the keyframes handed to the video model, and the per-damage
 * localization images. Always resolves for a completed job — a missing
 * artifact comes back as an empty section, which the viewer explains.
 */
export async function fetchVideoDebug(
  jobId: string,
  signal?: AbortSignal,
): Promise<VideoDebugResponse> {
  const res = await apiFetch(`/api/jobs/${encodeURIComponent(jobId)}/debug`, {
    app: 'video',
    signal,
  });
  return jsonOrThrow<VideoDebugResponse>(res);
}

/**
 * Parsed `generation_log.csv` — one row per physical FAL call attempt.
 * 404s for jobs that predate the log, so callers gate on the manifest's
 * `has_generation_log` rather than probing this endpoint.
 */
export async function fetchVideoGenerationLog(
  jobId: string,
  signal?: AbortSignal,
): Promise<GenerationLogResponse> {
  const res = await apiFetch(
    `/api/jobs/${encodeURIComponent(jobId)}/debug/generation-log`,
    { app: 'video', signal },
  );
  return jsonOrThrow<GenerationLogResponse>(res);
}

/**
 * One debug artifact, by the job-relative `path` from the manifest. The path
 * segments are encoded individually so the slashes survive.
 */
export function fetchVideoDebugImageBlobUrl(
  jobId: string,
  path: string,
  signal?: AbortSignal,
): Promise<string> {
  const encoded = path.split('/').map(encodeURIComponent).join('/');
  return fetchBlobUrl(
    `/api/jobs/${encodeURIComponent(jobId)}/debug/images/${encoded}`,
    signal,
  );
}

/**
 * Trigger a browser download of the rendered MP4. Goes through
 * `apiFetch` so the Bearer token is attached (you can't put a header
 * on `<a download>`), then drives a synthetic `<a>` click against a
 * temporary blob URL.
 */
export async function downloadVideo(jobId: string, scanId: string): Promise<void> {
  const url = await fetchVideoBlobUrl(jobId);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = `${scanId}_full_360.mp4`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoke after a tick so the browser has time to start the download.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

// VIDEO_API_BASE is re-exported so tests + dev tools can compose the same
// URL the SPA would; runtime code should use the helpers above.
export { VIDEO_API_BASE };
